import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCurrency } from "../App";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import {
  ArrowRight,
  CheckCircle,
  Zap,
  Users,
  Mail,
  Calendar,
  BarChart3,
  MessageSquare,
  Database,
  Workflow,
  Globe,
  Smartphone,
  Shield,
  Headphones,
} from "lucide-react";

const SoftwarePage = () => {
  const { formatPrice } = useCurrency();

  const features = [
    {
      icon: Database,
      title: "CRM & Contact Management",
      description: "Centralized database for all your contacts, leads, and customers with custom fields and tagging.",
    },
    {
      icon: Workflow,
      title: "Marketing Automation",
      description: "Build sophisticated email and SMS sequences that nurture leads automatically.",
    },
    {
      icon: Globe,
      title: "Funnel & Website Builder",
      description: "Create high-converting landing pages and funnels without any coding required.",
    },
    {
      icon: Calendar,
      title: "Booking & Scheduling",
      description: "Let prospects book appointments directly with integrated calendar and reminders.",
    },
    {
      icon: BarChart3,
      title: "Pipeline Management",
      description: "Visual sales pipelines that show exactly where every opportunity stands.",
    },
    {
      icon: MessageSquare,
      title: "Unified Inbox",
      description: "Manage all conversations—email, SMS, social—from one central inbox.",
    },
    {
      icon: Mail,
      title: "Email Marketing",
      description: "Send broadcasts, build templates, and track opens, clicks, and conversions.",
    },
    {
      icon: Smartphone,
      title: "SMS & WhatsApp",
      description: "Two-way messaging that keeps you connected with prospects and customers.",
    },
  ];

  const benefits = [
    "All-in-one platform eliminates tool sprawl",
    "White-labeled with your branding",
    "Unlimited contacts and users",
    "Dedicated support from OptimaGrowth",
    "Regular training and updates",
    "Integration with your existing tools",
  ];

  return (
    <div className="pt-32" data-testid="software-page">
      <SEO 
        title="CRM & Marketing Automation Software Platform"
        description="OptimaGrowth Software is an all-in-one CRM, marketing automation, and sales funnel platform with pipeline management, email automation, and AI chatbots."
        keywords="CRM software, marketing automation platform, sales funnel builder, email automation, WhatsApp marketing, landing page builder, appointment booking software"
      />
      <Breadcrumb items={[{ name: "Software" }]} />
      {/* Hero Section */}
      <section className="section" data-testid="software-hero">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="badge badge-cyan mb-4">OptimaGrowth Software</span>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-optima-dark mb-6">
                All-in-One CRM & Marketing Automation Platform
              </h1>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                OptimaGrowth Software is our proprietary growth and automation platform, built to give you everything you need to capture leads, nurture prospects, and close deals—all in one place.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Powered by enterprise-grade technology and fully white-labeled for your brand, this platform replaces your CRM, email marketing, landing pages, scheduling, and more.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="software-cta"
                >
                  Request a Demo <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="https://app.gohighlevel.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="software-login"
                >
                  Client Login
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800"
                alt="OptimaGrowth CRM and marketing automation software dashboard"
                className="rounded-2xl shadow-strong w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-medium p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-optima-dark">10+ Tools</div>
                    <div className="text-sm text-slate-500">In One Platform</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-alt" data-testid="software-features">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="badge badge-blue mb-4">Platform Features</span>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">
              Everything You Need to Grow
            </h2>
            <p className="text-slate-600 text-lg">
              Replace your disconnected tools with one integrated platform designed for growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-soft"
              >
                <div className="w-12 h-12 rounded-lg bg-optima-blue/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-optima-blue" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-optima-dark mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section" data-testid="software-benefits">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600"
                alt="Marketing automation platform benefits for business growth"
                className="rounded-2xl shadow-medium w-full"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-pink mb-4">Why OptimaGrowth Software</span>
              <h2 className="font-heading text-3xl font-bold text-optima-dark mb-6">
                Built for Your Success
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                OptimaGrowth Software isn't just another SaaS tool—it's the same platform we use to run our own marketing and help our clients grow. We've tested, refined, and optimized it for real-world results.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section-alt" data-testid="software-how">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="badge badge-cyan mb-4">How It Works</span>
            <h2 className="font-heading text-3xl font-bold text-optima-dark mb-6">
              Getting Started is Simple
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-optima-blue text-white flex items-center justify-center font-heading text-2xl font-bold">
                1
              </div>
              <h3 className="font-heading text-xl font-semibold text-optima-dark mb-3">
                Setup & Configuration
              </h3>
              <p className="text-slate-600">
                We configure the platform for your business, including your branding, integrations, and initial automations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-optima-blue text-white flex items-center justify-center font-heading text-2xl font-bold">
                2
              </div>
              <h3 className="font-heading text-xl font-semibold text-optima-dark mb-3">
                Training & Onboarding
              </h3>
              <p className="text-slate-600">
                Your team gets trained on the platform with recorded sessions and documentation for reference.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-optima-blue text-white flex items-center justify-center font-heading text-2xl font-bold">
                3
              </div>
              <h3 className="font-heading text-xl font-semibold text-optima-dark mb-3">
                Launch & Support
              </h3>
              <p className="text-slate-600">
                Go live with ongoing support from our team to ensure you get the most from the platform.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="section" data-testid="software-pricing">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-optima-blue to-optima-cyan rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-4">
                  Software Access Options
                </h2>
                <p className="text-white/90 mb-4">
                  OptimaGrowth Software is available as part of our Growth Partner and Scale & Automation retainer packages, or as a standalone subscription.
                </p>
                <div className="space-y-2 text-white/80">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Included with Growth Partner & Scale retainers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Standalone from {formatPrice(2000)}/month</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Custom enterprise pricing available</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 lg:items-end">
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
                  data-testid="software-pricing-link"
                >
                  View All Packages <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors"
                  data-testid="software-demo-link"
                >
                  Request a Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="section-alt" data-testid="software-support">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-blue mb-4">Dedicated Support</span>
              <h2 className="font-heading text-3xl font-bold text-optima-dark mb-6">
                You're Never on Your Own
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Unlike generic SaaS tools where you're left to figure things out yourself, OptimaGrowth Software comes with dedicated support from our team. We're invested in your success.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Headphones className="w-6 h-6 text-optima-blue flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-optima-dark">Priority Support</h4>
                    <p className="text-sm text-slate-600">Quick responses when you need help</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="w-6 h-6 text-optima-blue flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-optima-dark">Training Sessions</h4>
                    <p className="text-sm text-slate-600">Regular training for your team</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-optima-blue flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-optima-dark">Managed Updates</h4>
                    <p className="text-sm text-slate-600">We handle platform updates</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Workflow className="w-6 h-6 text-optima-blue flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-optima-dark">Custom Setup</h4>
                    <p className="text-sm text-slate-600">Configured for your workflow</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600"
                alt="OptimaGrowth dedicated support team for SEO and marketing automation clients"
                className="rounded-2xl shadow-medium w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-optima-dark" data-testid="software-cta-final">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to See the Platform in Action?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Book a demo and we'll show you how OptimaGrowth Software can streamline your sales and marketing.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
              data-testid="software-final-cta"
            >
              Request a Demo <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <Link to="/services" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Explore Services</Link>
              <Link to="/pricing" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">View Pricing</Link>
              <Link to="/case-studies" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Case Studies</Link>
              <Link to="/blog" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Read Our Blog</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SoftwarePage;
